import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { cancelMyBuddyInvite, inviteMyBuddy, listMyBuddies, removeMyBuddy, } from '@digiwedge/teetime-api-client';
const normaliseError = (err, fallback) => err instanceof Error ? err : new Error(fallback);
const directBuddyAddDisabledMessage = 'Direct buddy add by Player UUID is disabled. Use inviteBuddy with playerId, email, or phone so the invitee can consent.';
export const usePlayerBuddies = (options = {}) => {
    const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
    const enabled = options.enabled ?? true;
    const shouldFetch = useMemo(() => Boolean(enabled && isAuthenticated), [enabled, isAuthenticated]);
    const [buddies, setBuddies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mutatingBuddyId, setMutatingBuddyId] = useState(null);
    const [inviting, setInviting] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        if (!shouldFetch) {
            setBuddies([]);
            setMutatingBuddyId(null);
            setLoading(false);
            setError(null);
        }
    }, [shouldFetch]);
    const fetchBuddies = useCallback(async () => {
        if (!shouldFetch) {
            return;
        }
        if (isMountedRef.current) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await listMyBuddies();
            if (isMountedRef.current) {
                setBuddies(Array.isArray(response) ? response : []);
            }
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to load buddies');
            if (isMountedRef.current) {
                setError(normalised);
                setBuddies([]);
            }
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [shouldFetch]);
    useEffect(() => {
        if (!shouldFetch) {
            return;
        }
        void fetchBuddies();
    }, [fetchBuddies, shouldFetch]);
    const refetch = useCallback(async () => {
        await fetchBuddies();
    }, [fetchBuddies]);
    const addBuddy = useCallback(async (buddyIdRaw) => {
        const buddyId = buddyIdRaw?.trim();
        if (!buddyId) {
            throw new Error('Buddy ID is required');
        }
        if (!shouldFetch) {
            throw new Error('You must be logged in to manage buddies');
        }
        if (isMountedRef.current) {
            setMutatingBuddyId(buddyId);
            setError(null);
        }
        try {
            throw new Error(directBuddyAddDisabledMessage);
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to add buddy');
            if (isMountedRef.current) {
                setError(normalised);
            }
            throw normalised;
        }
        finally {
            if (isMountedRef.current) {
                setMutatingBuddyId((current) => (current === buddyId ? null : current));
            }
        }
    }, [shouldFetch]);
    const inviteBuddy = useCallback(async (contact) => {
        if (!contact.playerId && !contact.email && !contact.phone) {
            throw new Error('Player, email, or phone is required');
        }
        if (!shouldFetch) {
            throw new Error('You must be logged in to manage buddies');
        }
        if (isMountedRef.current) {
            setInviting(true);
            setError(null);
        }
        try {
            const result = await inviteMyBuddy(contact);
            if (result.status === 'added') {
                await fetchBuddies();
            }
            return result;
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to invite buddy');
            if (isMountedRef.current) {
                setError(normalised);
            }
            throw normalised;
        }
        finally {
            if (isMountedRef.current) {
                setInviting(false);
            }
        }
    }, [fetchBuddies, shouldFetch]);
    const cancelBuddyInvite = useCallback(async (inviteIdRaw) => {
        const inviteId = inviteIdRaw?.trim();
        if (!inviteId) {
            throw new Error('Invite ID is required');
        }
        if (!shouldFetch) {
            throw new Error('You must be logged in to manage buddies');
        }
        if (isMountedRef.current) {
            setMutatingBuddyId(inviteId);
            setError(null);
        }
        try {
            await cancelMyBuddyInvite(inviteId);
            if (isMountedRef.current) {
                setBuddies((current) => current.filter((entry) => entry.inviteId !== inviteId));
            }
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to cancel buddy invite');
            if (isMountedRef.current) {
                setError(normalised);
            }
            throw normalised;
        }
        finally {
            if (isMountedRef.current) {
                setMutatingBuddyId((current) => (current === inviteId ? null : current));
            }
        }
    }, [shouldFetch]);
    const removeBuddy = useCallback(async (buddyIdRaw) => {
        const buddyId = buddyIdRaw?.trim();
        if (!buddyId) {
            throw new Error('Buddy ID is required');
        }
        if (!shouldFetch) {
            throw new Error('You must be logged in to manage buddies');
        }
        if (isMountedRef.current) {
            setMutatingBuddyId(buddyId);
            setError(null);
        }
        try {
            await removeMyBuddy(buddyId);
            if (isMountedRef.current) {
                setBuddies((current) => current.filter((entry) => entry.buddyId !== buddyId));
            }
        }
        catch (err) {
            const normalised = normaliseError(err, 'Failed to remove buddy');
            if (isMountedRef.current) {
                setError(normalised);
            }
            throw normalised;
        }
        finally {
            if (isMountedRef.current) {
                setMutatingBuddyId((current) => (current === buddyId ? null : current));
            }
        }
    }, [shouldFetch]);
    return {
        buddies,
        loading,
        error,
        mutatingBuddyId,
        inviting,
        refetch,
        addBuddy,
        inviteBuddy,
        removeBuddy,
        cancelBuddyInvite,
    };
};
//# sourceMappingURL=usePlayerBuddies.js.map